import { Card, ModalDialog, Typography } from '@navi/web-ui/lib/primitives';
import { useSelector } from 'react-redux';
import { RootState } from 'src/store';
import styles from './index.module.scss';
import SuspenseLoader from 'src/components/SkeletonLoader/SuspenseLoader';
import LineLoader from 'src/components/SkeletonLoader/LineLoader';
import { useEffect, useState } from 'react';
import { formatDate } from 'src/utils/commonUtils';
import PanRejectionModal from './PanRejectionModal';
import { ISelectedApprovalItem } from '../../interface';
import cx from 'classnames';
import Actions from '../Actions';
import PanExpandedModal from './PanExpandedModal';
import NoResult from '../NoResult';

const PanCard = (props: ISelectedApprovalItem) => {
  const { handleApproval, handleRejection } = props;
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [showExpandImageModal, setShowExpandImageModal] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [panImageLoading, setPanImageLoading] = useState(false);

  const selectedPanApprovalItem = useSelector(
    (state: RootState) => state.approvals?.selectedPanApprovalItem
  );
  const loading = useSelector((state: RootState) => state.approvals?.loading);
  const totalPanPendingApprovals = useSelector(
    (state: RootState) => state.approvals?.totalPanPendingApprovals
  );
  const {
    agentName,
    optimizedSelfieDocumentUri,
    dateOfBirth = '',
    panNumber,
    originalPanDocumentUri,
    originalSelfieDocumentUri
  } = selectedPanApprovalItem?.data || {};

  const toggleRejectionModal = () => setShowRejectionModal(prev => !prev);

  const toggleImageExpandModal = () => {
    setShowExpandImageModal(prev => !prev);
  };

  useEffect(() => {
    if (originalPanDocumentUri) {
      setPanImageLoading(true);
    }
  }, [originalPanDocumentUri]);

  useEffect(() => {
    if (originalSelfieDocumentUri) {
      setImageLoading(true);
    }
  }, [originalSelfieDocumentUri]);

  if (!loading && !selectedPanApprovalItem) {
    return <NoResult totalPendingApprovals={totalPanPendingApprovals} />;
  }

  return (
    <div className={styles.selectedItem}>
      <Card className={styles.card}>
        <SuspenseLoader
          loading={loading}
          fallBack={
            <div className={cx(styles.avatarContainer, '!hover:opacity-100 !cursor-auto')}>
              <div className="flex gap-6">
                <LineLoader lineStyle={styles.avatar} />
                <LineLoader lineStyle={styles.avatarPan} />
              </div>
              <div className={styles.avatarTextContainer}>
                <LineLoader dimenshionUnit="px" lineWidth={50} childrenType="H6" />
              </div>
            </div>
          }
        >
          <div className={styles.avatarContainer} onClick={toggleImageExpandModal}>
            <div className="flex gap-6">
              <SuspenseLoader
                loading={imageLoading}
                fallBack={
                  <>
                    <img
                      className={styles.avatar}
                      src={optimizedSelfieDocumentUri}
                      loading="eager"
                    />
                    <img
                      className={styles.hiddenImage}
                      src={originalSelfieDocumentUri}
                      onLoad={() => setImageLoading(false)}
                      loading="eager"
                    />
                  </>
                }
              >
                <img src={originalSelfieDocumentUri} loading={'lazy'} className={styles.avatar} />
              </SuspenseLoader>
              <SuspenseLoader
                loading={panImageLoading}
                fallBack={
                  <>
                    <img
                      className={styles.avatarPan}
                      src={originalPanDocumentUri}
                      loading="eager"
                    />
                    <img
                      className={styles.hiddenImage}
                      src={originalPanDocumentUri}
                      onLoad={() => setPanImageLoading(false)}
                      loading="eager"
                    />
                  </>
                }
              >
                <img
                  src={originalPanDocumentUri}
                  loading={'lazy'}
                  className={cx(styles.avatarPan, 'bg-[var(--navi-color-gray-border)]')}
                />
              </SuspenseLoader>
            </div>
            <div className={styles.avatarTextContainer}>
              <Typography variant="h6" className={styles.avatarText}>
                View
              </Typography>
            </div>
          </div>
        </SuspenseLoader>
        <SuspenseLoader
          loading={loading}
          fallBack={
            <LineLoader
              containerStyle="my-1 mt-[10px]"
              lineWidth={400}
              dimenshionUnit="px"
              childrenType={'H2'}
            />
          }
        >
          <Typography variant="h2" className={styles.name}>
            {agentName}
          </Typography>
        </SuspenseLoader>
        <SuspenseLoader
          loading={loading}
          fallBack={<LineLoader lineWidth={400} dimenshionUnit="px" childrenType={'H3'} />}
        >
          <Typography variant="h3" color="var(--navi-color-navigation-blue-c3)">
            DOB: {formatDate(dateOfBirth, { year: 'numeric' })}
          </Typography>
        </SuspenseLoader>
        <SuspenseLoader
          loading={loading}
          fallBack={
            <LineLoader
              containerStyle="mt-1"
              lineWidth={400}
              dimenshionUnit="px"
              childrenType={'H3'}
            />
          }
        >
          <Typography variant="h3" color="var(--navi-color-navigation-blue-c3)" className="mt-1">
            PAN: {panNumber}
          </Typography>
        </SuspenseLoader>
      </Card>
      <Actions
        isLoading={loading}
        handleApproval={handleApproval}
        toggleRejectionModal={toggleRejectionModal}
      />
      <ModalDialog
        open={showRejectionModal}
        onClose={toggleRejectionModal}
        header="Select Rejection reason"
      >
        <PanRejectionModal handleRejection={handleRejection} closeModal={toggleRejectionModal} />
      </ModalDialog>
      <PanExpandedModal
        showExpandImageModal={showExpandImageModal}
        toggleImageExpandModal={toggleImageExpandModal}
      />
    </div>
  );
};

export default PanCard;
